import React, { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import IMask from 'imask';
import styles from './InternshipForm.module.scss';
import StatesContext from '../../context/states';
import FileField from './FileField';
import { Spinner } from './icons';
import { MAX_LETTER_LENGTH, EMAIL_RE, DOC_EXTS, VIDEO_EXTS } from './constants';

const InternshipForm = (props) => {
  const { title = 'Заявка на стажировку', onSubmit, vacancyId } = props;
  const { endpoint } = useContext(StatesContext) || {};

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    letter: '',
  });
  const [files, setFiles] = useState({ resume: null, video: null });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  // idle | sending | success | error
  const [status, setStatus] = useState('idle');
  const phoneRef = useRef(null);
  const letterRef = useRef(null);

  const autoResizeLetter = () => {
    const el = letterRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!phoneRef.current) return;
    const mask = IMask(phoneRef.current, {
      mask: '+{7} (000) 000-00-00',
      prepare: (appended, masked) => {
        if (appended === '8' && masked.value === '') return '';
        return appended;
      },
    });
    mask.on('accept', () => {
      setValues((prev) => ({ ...prev, phone: mask.value }));
      setErrors((prev) => ({ ...prev, phone: undefined }));
    });
    return () => mask.destroy();
  }, []);

  const handleChange = (field) => (e) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.firstName.trim()) next.firstName = 'Обязательное поле';
    if (!values.lastName.trim()) next.lastName = 'Обязательное поле';

    if (!values.email.trim()) next.email = 'Обязательное поле';
    else if (!EMAIL_RE.test(values.email.trim())) next.email = 'Неверный формат почты';

    if (!values.phone.trim()) next.phone = 'Обязательное поле';
    else if (values.phone.replace(/\D/g, '').length < 11)
      next.phone = 'Неверный формат телефона';

    if (!values.letter.trim()) next.letter = 'Обязательное поле';
    else if (values.letter.length > MAX_LETTER_LENGTH)
      next.letter = 'Превышен лимит символов для сопроводительного письма';

    if (!consent) next.consent = 'Необходимо согласие на обработку персональных данных';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!validate()) return;
    if (!endpoint) return;

    const hasResume = Boolean(files.resume);
    const hasVideo = Boolean(files.video);

    const varDefs = [];
    if (hasResume) varDefs.push('$resume: Upload!');
    if (hasVideo) varDefs.push('$video_resume: Upload!');
    const varDefStr = varDefs.length ? `(${varDefs.join(', ')})` : '';

    const query = `
      mutation${varDefStr} {
        createInternshipResponse(
          first_name: ${JSON.stringify(values.firstName)}
          last_name: ${JSON.stringify(values.lastName)}
          email: ${JSON.stringify(values.email)}
          phone: ${JSON.stringify(values.phone)}
          comment: ${JSON.stringify(values.letter)}
          ${hasResume ? 'resume: $resume' : ''}
          ${hasVideo ? 'video_resume: $video_resume' : ''}
        )
      }
    `;

    const variables = {};
    if (hasResume) variables.resume = null;
    if (hasVideo) variables.video_resume = null;

    const map = {};
    const fileParts = [];
    if (hasResume) {
      map[fileParts.length] = ['variables.resume'];
      fileParts.push(files.resume);
    }
    if (hasVideo) {
      map[fileParts.length] = ['variables.video_resume'];
      fileParts.push(files.video);
    }

    const formData = new FormData();
    formData.append('operations', JSON.stringify({ query, variables }));
    formData.append('map', JSON.stringify(map));
    fileParts.forEach((file, index) => formData.append(String(index), file));

    setStatus('sending');
    fetch(`${endpoint}/graphql`, {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.data?.createInternshipResponse) {
          setStatus('success');
          if (onSubmit) onSubmit({ ...values, ...files, vacancyId });
        } else {
          setStatus('error');
        }
      })
      .catch((error) => {
        console.error(error);
        setStatus('error');
      });
  };

  const letterOver = values.letter.length > MAX_LETTER_LENGTH;
  const letterError = letterOver
    ? 'Превышен лимит символов для сопроводительного письма'
    : errors.letter;

  const renderInput = (field, label, type, placeholder, ref) => (
    <div className={styles.field}>
      <span className={styles.label}>
        {label}
        <span className={styles.required}>*</span>
      </span>
      <input
        type={type}
        ref={ref}
        className={clsx(styles.input, errors[field] && styles.inputError)}
        placeholder={placeholder}
        value={ref ? undefined : values[field]}
        onChange={ref ? undefined : handleChange(field)}
      />
      {errors[field] && <span className={styles.errorText}>{errors[field]}</span>}
    </div>
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.fields}>
        <div className={styles.row}>
          {renderInput('firstName', 'Имя', 'text', 'Введите ваше имя')}
          {renderInput('lastName', 'Фамилия', 'text', 'Введите вашу фамилию')}
        </div>

        <div className={styles.row}>
          {renderInput('email', 'Почта', 'email', 'Введите вашу почту')}
          {renderInput('phone', 'Телефон', 'tel', '+7', phoneRef)}
        </div>

        <div className={styles.field}>
          <span className={styles.label}>
            Сопроводительное письмо
            <span className={styles.required}>*</span>
          </span>
          <div className={styles.textareaWrap}>
            <textarea
              ref={letterRef}
              className={clsx(
                styles.textarea,
                (errors.letter || letterOver) && styles.inputError
              )}
              placeholder="Расскажите о себе и своей мотивации"
              value={values.letter}
              onChange={(e) => {
                handleChange('letter')(e);
                autoResizeLetter();
              }}
            />
            <span className={clsx(styles.counter, letterOver && styles.counterError)}>
              {values.letter.length} / {MAX_LETTER_LENGTH}
            </span>
          </div>
          {letterError && <span className={styles.errorText}>{letterError}</span>}
        </div>

        <div className={styles.dropzoneRow}>
          <FileField
            id="internship-resume"
            label="Резюме"
            accept=".pdf,.doc,.docx"
            hint="PDF, DOC, DOCX до 10 МБ"
            dropText="Нажмите или перетащите файл для загрузки резюме"
            errorNoun="резюме"
            validExts={DOC_EXTS}
            onChange={(file) => setFiles((prev) => ({ ...prev, resume: file }))}
          />
          <FileField
            id="internship-video"
            label="Видеовизитка"
            accept="video/*,.mp4,.mov,.avi,.webm"
            hint="MP4, MOV, AVI до 10 МБ"
            dropText="Нажмите или перетащите файл для загрузки видеовизитки"
            errorNoun="видеовизитки"
            validExts={VIDEO_EXTS}
            onChange={(file) => setFiles((prev) => ({ ...prev, video: file }))}
          />
        </div>
      </div>

        <label htmlFor="internship-consent" className={styles.checkbox}>
          <input
            type="checkbox"
            id="internship-consent"
            name="consent"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              setErrors((prev) => ({ ...prev, consent: undefined }));
            }}
          />
          <span className={clsx(styles.checkboxIcon, errors.consent && styles.checkboxIconError)}>
            <svg
              width="13"
              height="10"
              viewBox="0 0 13 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.1288 7.89042L1.04606 4.78196L0 5.83675L4.1288 10L13 1.05479L11.9539 0L4.1288 7.89042Z"
                fill="#008359"
              />
            </svg>
          </span>
          <span className={styles.checkboxText}>
            Я согласен на{' '}
            <a
              href="/doc/Согласие на обработку персональных данных.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              обработку персональных данных
            </a>
          </span>
        </label>

      <div className={styles.footer}>
        <button
          type="submit"
          className={styles.submit}
          disabled={status === 'sending' || status === 'success'}
        >
          {status === 'sending' && <Spinner />}
          {status === 'idle' && 'Отправить заявку'}
          {status === 'sending' && 'Отправляем...'}
          {status === 'success' && 'Заявка отправлена!'}
          {status === 'error' && 'Ошибка, попробуйте ещё раз'}
        </button>
      </div>
    </form>
  );
};

export default InternshipForm;
